import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Gift, Heart, UserPlus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export const ReferralSection = () => {
  const [referralEmail, setReferralEmail] = useState("");
  const [referralName, setReferralName] = useState("");
  const [referralMessage, setReferralMessage] = useState("");
  const [sendingReferral, setSendingReferral] = useState(false);

  const handleSendReferral = async () => {
    if (!referralEmail.trim() || !referralEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingReferral(true);
    try {
      await axios.post(`${API}/settings/send-referral`, {
        recipient_email: referralEmail.trim(),
        recipient_name: referralName.trim(),
        personal_message: referralMessage.trim(),
        app_url: window.location.origin,
      }, { withCredentials: true });
      toast.success(`Invitation sent to ${referralEmail}`);
      setReferralEmail("");
      setReferralName("");
      setReferralMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingReferral(false);
    }
  };

  return (
    <Card className="card-surface" data-testid="referral-card">
      <CardHeader style={{ borderBottom: '1px solid var(--t-border)', padding: '16px 20px' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)' }}>
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg font-semibold" style={{ color: 'var(--t-text)', fontFamily: 'var(--t-heading-font)' }}>
              Invite a Friend
            </CardTitle>
            <CardDescription style={{ color: 'var(--t-text-muted)' }}>Share the love — help a fellow agent get organized</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
          Know someone who could benefit from streamlined transaction management? Send them a personalized invitation to try it out — free.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Their Name</Label>
            <Input placeholder="Jane Smith" value={referralName} onChange={(e) => setReferralName(e.target.value)} data-testid="referral-name" />
          </div>
          <div>
            <Label className="text-xs font-medium mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Their Email *</Label>
            <Input type="email" placeholder="jane@example.com" value={referralEmail} onChange={(e) => setReferralEmail(e.target.value)} data-testid="referral-email" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Personal Message (optional)</Label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            rows={3}
            placeholder="Hey! I've been using this to manage my transactions and it's been a game changer..."
            value={referralMessage}
            onChange={(e) => setReferralMessage(e.target.value)}
            data-testid="referral-message"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSendReferral}
            disabled={sendingReferral || !referralEmail.trim()}
            className="text-white gap-2"
            style={{ background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)' }}
            data-testid="send-referral-btn"
          >
            {sendingReferral ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
            ) : (
              <><UserPlus className="w-4 h-4" /> Send Invitation</>
            )}
          </Button>
          <span className="text-xs flex items-center gap-1" style={{ color: 'var(--t-text-subtle)' }}>
            <Heart className="w-3 h-3" /> Sent via your email
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
